import React from 'react'
import { Platform } from 'react-native'

import { FormProvider } from 'react-hook-form'
import { useIntl } from 'react-intl'

import Button from 'app/components/base/Button'
import Checkbox from 'app/components/base/Checkbox'
import Link from 'app/components/base/Link/Link'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import AppleButton from 'app/components/buttons/AppleButton/AppleButton'
import FacebookButton from 'app/components/buttons/FacebookButton'
import GoogleButton from 'app/components/buttons/GoogleButton'
import PhoneEmailButtonGroup from 'app/components/common/PhoneEmailButtonGroup'
import PasswordField from 'app/components/fields/PasswordField'
import PhoneNumberField from 'app/components/fields/PhoneNumberField'
import TextField from 'app/components/fields/TextField'
import { IS_IOS_APP } from 'app/utils/constants/env.constants'

import ReadTermLabel from '../SignUpBusinessForm/components/ReadTermLabel'

import { useSignUpPersonalForm } from './hooks/useSignUpPersonalForm'

const SignUpPersonalForm = () => {
  const { formatMessage } = useIntl()

  const {
    handleChangeForm,
    isPhoneForm,
    isReadTerm,
    isSubmitting,
    methods,
    onSubmit,
    setIsReadTerm
  } = useSignUpPersonalForm()

  return (
    <FormProvider {...methods}>
      <Stack direction="column">
        <Text variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
          {formatMessage({ defaultMessage: 'Create personal account' })}
        </Text>

        <Stack direction="column" spacing={3}>
          <Text variant="body1" color="text.third" sx={{ textAlign: 'center' }}>
            {formatMessage({
              defaultMessage:
                'To sell your parts you need to login or create one now, it takes a 60 seconds.'
            })}
          </Text>

          <Stack direction="column" spacing={2}>
            <PhoneEmailButtonGroup
              isPhone={isPhoneForm}
              handleChange={handleChangeForm}
            />

            <TextField
              name="first_name"
              label={formatMessage({ defaultMessage: 'First Name' })}
            />

            <TextField
              name="last_name"
              label={formatMessage({ defaultMessage: 'Last Name' })}
            />

            {isPhoneForm ? (
              <PhoneNumberField
                key="phone"
                name="phone_number"
                label={formatMessage({ defaultMessage: 'Phone Number' })}
              />
            ) : (
              <TextField
                key="email"
                name="email"
                label={formatMessage({ defaultMessage: 'Email' })}
              />
            )}

            <Stack direction="column" spacing={1}>
              <PasswordField
                name="password"
                label={formatMessage({ defaultMessage: 'Password' })}
                helperText={formatMessage({
                  defaultMessage:
                    'Required min 8 symbols: Lowercase, Uppercase, Number'
                })}
              />

              <PasswordField
                name="password_confirmation"
                label={formatMessage({ defaultMessage: 'Confirm Password' })}
              />

              <Stack spacing={1} sx={{ py: 0.5 }} direction="row">
                <Checkbox
                  checked={isReadTerm}
                  {...Platform.select({
                    web: {
                      onChange: (_, checked) => setIsReadTerm(checked)
                    },
                    native: {
                      onChange: (value) => setIsReadTerm(!!value)
                    }
                  })}
                  sx={{ p: 0, height: '18px' }}
                />

                <ReadTermLabel />
              </Stack>
            </Stack>
          </Stack>

          <Button
            variant="contained"
            disabled={!isReadTerm}
            loading={isSubmitting}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {formatMessage({ defaultMessage: 'Sign up' })}
          </Button>
        </Stack>

        <Text
          variant="body2"
          sx={{ textAlign: 'center', mt: 4, mb: 2, color: 'text.third' }}
        >
          {formatMessage({ defaultMessage: 'or sign up with' })}
        </Text>

        <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
          <FacebookButton variant="register" />

          <GoogleButton variant="register" />

          {IS_IOS_APP && <AppleButton variant="register" />}
        </Stack>

        <Stack
          direction="row"
          sx={{ textAlign: 'center', justifyContent: 'center' }}
          spacing={0.5}
        >
          <Text variant="body2">
            {formatMessage({ defaultMessage: 'Already have an account?' })}
          </Text>

          <Link
            href="/sign-in"
            variant="body2"
            sx={{ fontWeight: 500, color: 'text.primary' }}
          >
            {formatMessage({ defaultMessage: 'Sign in' })}
          </Link>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

export default SignUpPersonalForm
